import { useState, useEffect, useCallback } from 'react';
import { assetsAPI } from '../../../api';
import { Button } from '../../../components/ui/Button';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Card, CardContent } from '../../../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import { Download, Archive, RotateCcw, Ban, Upload, FileText } from 'lucide-react';
import {
  DOCUMENT_TYPES,
  labelFor,
  formatAssetDate,
  formatFileSize,
} from '../../../lib/assetConstants';
import { useToast } from '../../../hooks/useToast';

const selectClass =
  'flex h-9 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const AssetDocumentsPanel = ({ assetId }) => {
  const { addToast } = useToast();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [uploadType, setUploadType] = useState('other');
  const [uploadNotes, setUploadNotes] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const fetchDocuments = useCallback(async () => {
    if (!assetId) return;
    try {
      setLoading(true);
      const res = await assetsAPI.getDocuments(assetId, {
        includeArchived: includeArchived || undefined,
      });
      setDocuments(res.documents || []);
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to load documents' });
    } finally {
      setLoading(false);
    }
  }, [assetId, includeArchived, addToast]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      addToast({ type: 'error', message: 'Please select a file' });
      return;
    }
    try {
      setUploading(true);
      await assetsAPI.uploadDocument(assetId, selectedFile, {
        documentType: uploadType,
        notes: uploadNotes.trim() || undefined,
      });
      addToast({ type: 'success', message: 'Document uploaded' });
      setSelectedFile(null);
      setUploadNotes('');
      e.target.reset?.();
      fetchDocuments();
    } catch (err) {
      addToast({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Upload failed',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const blob = await assetsAPI.getDocumentFile(assetId, doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName || 'document';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Download failed' });
    }
  };

  const handleArchive = async (docId) => {
    if (!confirm('Archive this document? The file will be retained.')) return;
    try {
      await assetsAPI.archiveDocument(assetId, docId);
      addToast({ type: 'success', message: 'Document archived' });
      fetchDocuments();
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to archive' });
    }
  };

  const handleVoid = async (docId) => {
    const reason = prompt('Reason for voiding this document:');
    if (!reason?.trim()) return;
    try {
      await assetsAPI.voidDocument(assetId, docId, reason.trim());
      addToast({ type: 'success', message: 'Document voided' });
      fetchDocuments();
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to void' });
    }
  };

  const handleRestore = async (docId) => {
    try {
      await assetsAPI.restoreDocument(assetId, docId);
      addToast({ type: 'success', message: 'Document restored' });
      fetchDocuments();
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to restore' });
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-white dark:bg-gray-800">
        <CardContent className="p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Upload document
          </h3>
          <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormLabel className="text-gray-700 dark:text-gray-300">Document type</FormLabel>
              <select value={uploadType} onChange={(e) => setUploadType(e.target.value)} className={selectClass}>
                {DOCUMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <FormLabel className="text-gray-700 dark:text-gray-300">File</FormLabel>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 dark:file:bg-blue-900/30 dark:file:text-blue-300"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">PDF, JPEG, PNG or WebP — max 10 MB</p>
            </div>
            <div className="md:col-span-2">
              <FormLabel className="text-gray-700 dark:text-gray-300">Notes (optional)</FormLabel>
              <input
                type="text"
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
                className="flex h-9 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <Button type="submit" variant="primary" disabled={uploading || !selectedFile}>
                {uploading ? 'Uploading…' : 'Upload'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Documents ({documents.length})
        </h3>
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600"
          />
          Show archived / voided
        </label>
      </div>

      <Card className="overflow-hidden bg-white dark:bg-gray-800">
        {loading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading documents…</div>
        ) : documents.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">No documents yet</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>File</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell className="text-sm">{labelFor(DOCUMENT_TYPES, doc.documentType)}</TableCell>
                  <TableCell className="text-sm text-gray-900 dark:text-gray-100">{doc.fileName}</TableCell>
                  <TableCell className="text-sm">{formatFileSize(doc.fileSize)}</TableCell>
                  <TableCell className="text-sm">{formatAssetDate(doc.createdAt)}</TableCell>
                  <TableCell>
                    {doc.isVoided ? (
                      <span className="text-xs px-2 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">Voided</span>
                    ) : doc.isArchived ? (
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">Archived</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">Active</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="icon" size="sm" onClick={() => handleDownload(doc)} title="Download">
                        <Download className="h-4 w-4" />
                      </Button>
                      {!doc.isArchived && !doc.isVoided && (
                        <>
                          <Button variant="icon" size="sm" onClick={() => handleArchive(doc.id)} title="Archive">
                            <Archive className="h-4 w-4" />
                          </Button>
                          <Button variant="icon" size="sm" onClick={() => handleVoid(doc.id)} title="Void">
                            <Ban className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {(doc.isArchived || doc.isVoided) && (
                        <Button variant="icon" size="sm" onClick={() => handleRestore(doc.id)} title="Restore">
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
};

export default AssetDocumentsPanel;
